import { useState } from 'react';
import {
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Body2,
  Badge,
  Button,
  Divider,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ChevronDownRegular,
  ChevronUpRegular,
} from '@fluentui/react-icons';
import type { QualificationStatus } from '../types';

interface QualificationGateBannerProps {
  qualification: QualificationStatus;
}

const useStyles = makeStyles({
  bannerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
  },
  criteriaList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalL,
  },
  criterionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  criterionLabel: {
    minWidth: '200px',
  },
  expandSection: {
    marginTop: tokens.spacingVerticalL,
  },
  howToQualify: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalS,
  },
});

export function QualificationGateBanner({ qualification }: QualificationGateBannerProps) {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);

  if (qualification.isQualified) return null;

  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <div className={styles.bannerContent}>
          <MessageBarTitle>Qualification Required</MessageBarTitle>
          <Text>
            Your organization meets {qualification.criteriaMet} of {qualification.criteriaRequired} required
            criteria to run the diagnostic. Meet at least {qualification.criteriaRequired} criteria to unlock
            recalculation and report generation.
          </Text>

          <div className={styles.criteriaList}>
            {qualification.criteria.map((criterion) => (
              <div key={criterion.key} className={styles.criterionRow}>
                {criterion.isMet ? (
                  <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '16px' }} />
                ) : (
                  <DismissCircleRegular style={{ color: tokens.colorPaletteRedForeground1, fontSize: '16px' }} />
                )}
                <Body2 className={styles.criterionLabel}>
                  {criterion.label}
                </Body2>
                <Badge
                  appearance="tint"
                  color={criterion.isMet ? 'success' : 'informative'}
                  size="medium"
                >
                  {criterion.currentValue}
                </Badge>
              </div>
            ))}
          </div>

          <div className={styles.expandSection}>
            <Button
              appearance="subtle"
              size="medium"
              icon={expanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Hide details' : 'How to qualify'}
            </Button>

            {expanded && (
              <>
                <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />
                <div className={styles.howToQualify}>
                  <Body2>To qualify, ensure your organization meets at least 2 of these criteria:</Body2>
                  <Body2>1. Invite 20+ team members to your organization</Body2>
                  <Body2>2. Connect 5+ repositories via integrations</Body2>
                  <Body2>3. Set up a CI/CD integration (GitHub, GitLab, or Azure DevOps)</Body2>
                  <Body2>4. Enable a qualifying compliance framework (SOC2, ISO 27001, APRA, or CPS 234)</Body2>
                  <Body2>5. Assign a Security Engineer, Admin, or Owner role to a team member</Body2>
                </div>
              </>
            )}
          </div>
        </div>
      </MessageBarBody>
    </MessageBar>
  );
}
